/* eslint-disable no-restricted-imports */
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  Cancel as CancelIcon,
  Description as DescriptionIcon,
  LocalShipping as ShippingIcon,
  Search as SearchIcon,
} from '@material-ui/icons';

import { Skeleton } from '@mui/material';
import { contigoService } from 'services';
import { API_URL } from 'settings';

export default function ShippingTab() {
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Available invoices
  const [availableInvoices, setAvailableInvoices] = useState([]);

  // Shipments list
  const [shipments, setShipments] = useState([]);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);

  // Selected invoices with volumetric data
  const [selectedInvoices, setSelectedInvoices] = useState([]);

  // Search filters
  const [invoicesSearch, setInvoicesSearch] = useState('');
  const [shipmentsSearch, setShipmentsSearch] = useState('');

  // Pagination for available invoices
  const [invoicesPage, setInvoicesPage] = useState(0);
  const [invoicesRowsPerPage, setInvoicesRowsPerPage] = useState(5);

  // Pagination for shipments
  const [shipmentsPage, setShipmentsPage] = useState(0);
  const [shipmentsRowsPerPage, setShipmentsRowsPerPage] = useState(5);

  // Shipping details
  const [shippingMethod, setShippingMethod] = useState('CORREIOS');
  const [trackingCode, setTrackingCode] = useState('');
  const [carrierName, setCarrierName] = useState('');
  const [shipmentNotes, setShipmentNotes] = useState('');

  // Dialog states
  const [viewShipmentDialog, setViewShipmentDialog] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [shipmentToCancel, setShipmentToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadAvailableInvoices();
    loadShipments();
  }, []);

  const loadAvailableInvoices = async () => {
    try {
      setLoading(true);
      const response = await contigoService.getAvailableInvoicesForShipping();
      setAvailableInvoices(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar notas disponíveis:', error);
      toast.error('Erro ao carregar notas fiscais disponíveis');
    } finally {
      setLoading(false);
    }
  };

  const loadShipments = async () => {
    try {
      setShipmentsLoading(true);
      const response = await contigoService.listShipments();
      setShipments(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar expedições:', error);
      toast.error('Erro ao carregar lista de expedições');
    } finally {
      setShipmentsLoading(false);
    }
  };

  const handleToggleInvoice = (invoice) => {
    const existingIndex = selectedInvoices.findIndex((i) => i.invoice_id === invoice.id);

    if (existingIndex >= 0) {
      setSelectedInvoices(selectedInvoices.filter((i) => i.invoice_id !== invoice.id));
    } else {
      setSelectedInvoices([
        ...selectedInvoices,
        {
          invoice_id: invoice.id,
          invoice_number: invoice.invoice_number,
          order_number: invoice.order_number,
          customer_name: invoice.customer_name,
          volumes: 1,
          weight: 0,
          dimensions: '',
          notes: '',
        },
      ]);
    }
  };

  const isInvoiceSelected = (invoiceId) => {
    return selectedInvoices.some((i) => i.invoice_id === invoiceId);
  };

  const handleVolumetricChange = (invoiceId, field, value) => {
    setSelectedInvoices((prev) =>
      prev.map((invoice) => {
        if (invoice.invoice_id === invoiceId) {
          return { ...invoice, [field]: value };
        }
        return invoice;
      }),
    );
  };

  const handleCreateShipment = async () => {
    if (selectedInvoices.length === 0) {
      toast.warning('Selecione pelo menos uma nota fiscal');
      return;
    }

    if (shippingMethod === 'CORREIOS' && !trackingCode.trim()) {
      toast.warning('Código de rastreio é obrigatório para Correios');
      return;
    }

    if (shippingMethod === 'TRANSPORTADORA' && !carrierName.trim()) {
      toast.warning('Nome da transportadora é obrigatório');
      return;
    }

    try {
      setCreating(true);

      const payload = {
        shipping_method: shippingMethod,
        tracking_code: shippingMethod === 'CORREIOS' ? trackingCode : undefined,
        carrier_name: shippingMethod === 'TRANSPORTADORA' ? carrierName : undefined,
        notes: shipmentNotes,
        items: selectedInvoices.map((inv) => ({
          invoice_id: inv.invoice_id,
          volumes: parseInt(inv.volumes) || 1,
          weight: parseFloat(inv.weight) || undefined,
          dimensions: inv.dimensions || undefined,
          notes: inv.notes || undefined,
        })),
      };

      const response = await contigoService.createShipment(payload);

      toast.success('Expedição criada com sucesso!');

      // Reset form
      setSelectedInvoices([]);
      setTrackingCode('');
      setCarrierName('');
      setShipmentNotes('');
      setShippingMethod('CORREIOS');

      // Reload data
      await loadAvailableInvoices();
      await loadShipments();

      // Open PDF in new tab
      if (response.data.pdf_path) {
        const pdfUrl = `${API_URL}${response.data.pdf_path}`;
        window.open(pdfUrl, '_blank');
      }
    } catch (error) {
      console.error('Erro ao criar expedição:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar expedição');
    } finally {
      setCreating(false);
    }
  };

  const handleViewShipment = async (shipmentId) => {
    try {
      const response = await contigoService.getShipment(shipmentId);
      setSelectedShipment(response.data);
      setViewShipmentDialog(true);
    } catch (error) {
      console.error('Erro ao carregar detalhes da expedição:', error);
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleOpenCancelDialog = (shipment) => {
    setShipmentToCancel(shipment);
    setCancelReason('');
    setCancelDialog(true);
  };

  const handleCancelShipment = async () => {
    if (!shipmentToCancel) return;

    try {
      setCancelling(true);
      await contigoService.cancelShipment(shipmentToCancel.id, cancelReason);
      toast.success('Expedição cancelada com sucesso');
      setCancelDialog(false);
      setShipmentToCancel(null);
      setCancelReason('');
      await loadShipments();
      await loadAvailableInvoices();
    } catch (error) {
      console.error('Erro ao cancelar expedição:', error);
      toast.error('Erro ao cancelar expedição');
    } finally {
      setCancelling(false);
    }
  };

  const handleDownloadPDF = (pdfPath) => {
    const url = `${API_URL}${pdfPath}`;
    window.open(url, '_blank');
  };

  const handleChangeInvoicesPage = (event, newPage) => {
    setInvoicesPage(newPage);
  };

  const handleChangeInvoicesRowsPerPage = (event) => {
    setInvoicesRowsPerPage(parseInt(event.target.value, 10));
    setInvoicesPage(0);
  };

  const handleChangeShipmentsPage = (event, newPage) => {
    setShipmentsPage(newPage);
  };

  const handleChangeShipmentsRowsPerPage = (event) => {
    setShipmentsRowsPerPage(parseInt(event.target.value, 10));
    setShipmentsPage(0);
  };

  // Filtered data
  const filteredInvoices = availableInvoices.filter((invoice) => {
    if (!invoicesSearch) return true;
    const searchLower = invoicesSearch.toLowerCase();
    return (
      invoice.invoice_number?.toLowerCase().includes(searchLower) ||
      invoice.order_number?.toLowerCase().includes(searchLower) ||
      invoice.customer_name?.toLowerCase().includes(searchLower)
    );
  });

  const filteredShipments = shipments.filter((shipment) => {
    if (!shipmentsSearch) return true;
    const searchLower = shipmentsSearch.toLowerCase();
    return (
      shipment.shipment_number?.toLowerCase().includes(searchLower) ||
      shipment.shipping_method?.toLowerCase().includes(searchLower) ||
      shipment.status?.toLowerCase().includes(searchLower)
    );
  });

  // Paginated data
  const paginatedInvoices = filteredInvoices.slice(
    invoicesPage * invoicesRowsPerPage,
    invoicesPage * invoicesRowsPerPage + invoicesRowsPerPage,
  );

  const paginatedShipments = filteredShipments.slice(
    shipmentsPage * shipmentsRowsPerPage,
    shipmentsPage * shipmentsRowsPerPage + shipmentsRowsPerPage,
  );

  return (
    <>
      <CardContent>
        <Grid container spacing={3}>
          {/* Create Shipment Section */}
          <Grid item xs={12} lg={6}>
            <Typography variant='h6' gutterBottom>
              Nova Expedição
            </Typography>
            <Divider style={{ marginBottom: 16 }} />

            {loading ? (
              <Box>
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
              </Box>
            ) : (
              <>
                <Typography variant='subtitle2' gutterBottom>
                  Notas Fiscais Disponíveis
                </Typography>

                <TextField
                  fullWidth
                  size='small'
                  placeholder='Buscar por NF, Pedido ou Cliente...'
                  variant='outlined'
                  value={invoicesSearch}
                  onChange={(e) => setInvoicesSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  style={{ marginBottom: 12 }}
                />

                {availableInvoices.length === 0 ? (
                  <Typography variant='body2' color='textSecondary' style={{ marginBottom: 16 }}>
                    Nenhuma nota fiscal disponível para expedição
                  </Typography>
                ) : (
                  <>
                    <TableContainer component={Paper} variant='outlined'>
                      <Table size='small'>
                        <TableHead>
                          <TableRow>
                            <TableCell padding='checkbox' />
                            <TableCell>NF</TableCell>
                            <TableCell>Pedido</TableCell>
                            <TableCell>Cliente</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedInvoices.map((invoice) => (
                            <TableRow key={invoice.id} hover>
                              <TableCell padding='checkbox'>
                                <Checkbox
                                  checked={isInvoiceSelected(invoice.id)}
                                  onChange={() => handleToggleInvoice(invoice)}
                                />
                              </TableCell>
                              <TableCell>{invoice.invoice_number}</TableCell>
                              <TableCell>{invoice.order_number}</TableCell>
                              <TableCell>{invoice.customer_name}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <TablePagination
                        component='div'
                        count={filteredInvoices.length}
                        page={invoicesPage}
                        onPageChange={handleChangeInvoicesPage}
                        rowsPerPage={invoicesRowsPerPage}
                        onRowsPerPageChange={handleChangeInvoicesRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25]}
                        labelRowsPerPage='Linhas por página:'
                        labelDisplayedRows={({ from, to, count }) =>
                          `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                        }
                      />
                    </TableContainer>
                    <Box style={{ marginBottom: 16 }} />
                  </>
                )}

                {selectedInvoices.length > 0 && (
                  <>
                    <Typography variant='subtitle2' gutterBottom style={{ marginTop: 24 }}>
                      Dados Volumétricos
                    </Typography>

                    {selectedInvoices.map((invoice) => (
                      <Card
                        key={invoice.invoice_id}
                        variant='outlined'
                        style={{ marginBottom: 12, padding: 12 }}
                      >
                        <Typography variant='body2' style={{ fontWeight: 'bold', marginBottom: 8 }}>
                          NF {invoice.invoice_number} - Pedido {invoice.order_number}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              label='Volumes'
                              type='number'
                              fullWidth
                              size='small'
                              value={invoice.volumes}
                              onChange={(e) =>
                                handleVolumetricChange(
                                  invoice.invoice_id,
                                  'volumes',
                                  e.target.value,
                                )
                              }
                              inputProps={{ min: 1 }}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              label='Peso (kg)'
                              type='number'
                              fullWidth
                              size='small'
                              value={invoice.weight}
                              onChange={(e) =>
                                handleVolumetricChange(invoice.invoice_id, 'weight', e.target.value)
                              }
                              inputProps={{ min: 0, step: 0.1 }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label='Dimensões'
                              fullWidth
                              size='small'
                              placeholder='ex: 30x40x50cm'
                              value={invoice.dimensions}
                              onChange={(e) =>
                                handleVolumetricChange(
                                  invoice.invoice_id,
                                  'dimensions',
                                  e.target.value,
                                )
                              }
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label='Observações'
                              fullWidth
                              size='small'
                              multiline
                              minRows={2}
                              value={invoice.notes}
                              onChange={(e) =>
                                handleVolumetricChange(invoice.invoice_id, 'notes', e.target.value)
                              }
                            />
                          </Grid>
                        </Grid>
                      </Card>
                    ))}

                    <Divider style={{ margin: '24px 0' }} />

                    <Typography variant='subtitle2' gutterBottom>
                      Método de Envio
                    </Typography>
                    <FormControl component='fieldset' style={{ marginBottom: 16 }}>
                      <RadioGroup
                        row
                        value={shippingMethod}
                        onChange={(e) => setShippingMethod(e.target.value)}
                      >
                        <FormControlLabel value='CORREIOS' control={<Radio />} label='Correios' />
                        <FormControlLabel
                          value='TRANSPORTADORA'
                          control={<Radio />}
                          label='Transportadora'
                        />
                      </RadioGroup>
                    </FormControl>

                    {shippingMethod === 'CORREIOS' && (
                      <TextField
                        label='Código de Rastreio'
                        fullWidth
                        value={trackingCode}
                        onChange={(e) => setTrackingCode(e.target.value)}
                        placeholder='AA123456789BR'
                        style={{ marginBottom: 16 }}
                      />
                    )}

                    {shippingMethod === 'TRANSPORTADORA' && (
                      <TextField
                        label='Nome da Transportadora'
                        fullWidth
                        value={carrierName}
                        onChange={(e) => setCarrierName(e.target.value)}
                        placeholder='Ex: Transportadora XYZ'
                        style={{ marginBottom: 16 }}
                      />
                    )}

                    <TextField
                      label='Observações da Expedição'
                      fullWidth
                      multiline
                      minRows={3}
                      value={shipmentNotes}
                      onChange={(e) => setShipmentNotes(e.target.value)}
                      style={{ marginBottom: 16 }}
                    />

                    <Button
                      variant='contained'
                      color='primary'
                      fullWidth
                      onClick={handleCreateShipment}
                      disabled={creating}
                      startIcon={creating ? <CircularProgress size={20} /> : <ShippingIcon />}
                    >
                      {creating ? 'Criando Expedição...' : 'Criar Expedição e Gerar PDF'}
                    </Button>
                  </>
                )}
              </>
            )}
          </Grid>

          {/* Shipments List Section */}
          <Grid item xs={12} lg={6}>
            <Typography variant='h6' gutterBottom>
              Expedições Criadas
            </Typography>
            <Divider style={{ marginBottom: 16 }} />

            <TextField
              fullWidth
              size='small'
              placeholder='Buscar por Número, Método ou Status...'
              variant='outlined'
              value={shipmentsSearch}
              onChange={(e) => setShipmentsSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              style={{ marginBottom: 12 }}
            />

            {shipmentsLoading ? (
              <Box>
                <Skeleton height={60} />
                <Skeleton height={60} />
                <Skeleton height={60} />
              </Box>
            ) : shipments.length === 0 ? (
              <Typography variant='body2' color='textSecondary'>
                Nenhuma expedição criada ainda
              </Typography>
            ) : (
              <TableContainer component={Paper} variant='outlined'>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Número</TableCell>
                      <TableCell>Método</TableCell>
                      <TableCell>Volumes</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align='right'>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedShipments.map((shipment) => (
                      <TableRow key={shipment.id} hover>
                        <TableCell>{shipment.shipment_number}</TableCell>
                        <TableCell>
                          <Chip
                            label={shipment.shipping_method}
                            size='small'
                            color={shipment.shipping_method === 'CORREIOS' ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{shipment.total_volumes}</TableCell>
                        <TableCell>
                          <Chip
                            label={shipment.status}
                            size='small'
                            color={shipment.status === 'ACTIVE' ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <Button
                            size='small'
                            startIcon={<DescriptionIcon />}
                            onClick={() => handleViewShipment(shipment.id)}
                            style={{ marginRight: 8 }}
                          >
                            Ver
                          </Button>
                          {shipment.status === 'ACTIVE' && (
                            <Button
                              size='small'
                              color='secondary'
                              startIcon={<CancelIcon />}
                              onClick={() => handleOpenCancelDialog(shipment)}
                            >
                              Cancelar
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  component='div'
                  count={filteredShipments.length}
                  page={shipmentsPage}
                  onPageChange={handleChangeShipmentsPage}
                  rowsPerPage={shipmentsRowsPerPage}
                  onRowsPerPageChange={handleChangeShipmentsRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25]}
                  labelRowsPerPage='Linhas por página:'
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                  }
                />
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </CardContent>

      {/* View Shipment Dialog */}
      <Dialog
        open={viewShipmentDialog}
        onClose={() => setViewShipmentDialog(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Detalhes da Expedição</DialogTitle>
        <DialogContent>
          {selectedShipment && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant='subtitle2' color='textSecondary'>
                    Número da Expedição
                  </Typography>
                  <Typography variant='body1'>{selectedShipment.shipment_number}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='subtitle2' color='textSecondary'>
                    Método de Envio
                  </Typography>
                  <Typography variant='body1'>{selectedShipment.shipping_method}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='subtitle2' color='textSecondary'>
                    {selectedShipment.shipping_method === 'CORREIOS'
                      ? 'Código de Rastreio'
                      : 'Transportadora'}
                  </Typography>
                  <Typography variant='body1'>
                    {selectedShipment.tracking_code || selectedShipment.carrier_name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='subtitle2' color='textSecondary'>
                    Status
                  </Typography>
                  <Chip
                    label={selectedShipment.status}
                    color={selectedShipment.status === 'ACTIVE' ? 'primary' : 'default'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider style={{ margin: '16px 0' }} />
                  <Typography variant='h6' gutterBottom>
                    Notas Fiscais
                  </Typography>
                  <TableContainer component={Paper} variant='outlined'>
                    <Table size='small'>
                      <TableHead>
                        <TableRow>
                          <TableCell>NF</TableCell>
                          <TableCell>Volumes</TableCell>
                          <TableCell>Peso (kg)</TableCell>
                          <TableCell>Dimensões</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(selectedShipment.items || []).map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>{item.invoice_number}</TableCell>
                            <TableCell>{item.volumes}</TableCell>
                            <TableCell>{item.weight ? item.weight.toFixed(2) : '-'}</TableCell>
                            <TableCell>{item.dimensions || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                {selectedShipment.notes && (
                  <Grid item xs={12}>
                    <Typography variant='subtitle2' color='textSecondary'>
                      Observações
                    </Typography>
                    <Typography variant='body2'>{selectedShipment.notes}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedShipment?.dispatch_pdf_path && (
            <Button
              onClick={() => handleDownloadPDF(selectedShipment.dispatch_pdf_path)}
              color='primary'
              startIcon={<DescriptionIcon />}
            >
              Baixar PDF
            </Button>
          )}
          <Button onClick={() => setViewShipmentDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Shipment Dialog */}
      <Dialog
        open={cancelDialog}
        onClose={() => !cancelling && setCancelDialog(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Cancelar Expedição</DialogTitle>
        <DialogContent>
          <Typography variant='body2' gutterBottom>
            Tem certeza que deseja cancelar a expedição{' '}
            <strong>{shipmentToCancel?.shipment_number}</strong>?
          </Typography>
          <Typography variant='body2' color='textSecondary' gutterBottom style={{ marginTop: 8 }}>
            As notas fiscais ficarão disponíveis para nova expedição.
          </Typography>
          <TextField
            label='Motivo do Cancelamento (opcional)'
            fullWidth
            multiline
            minRows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            style={{ marginTop: 16 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(false)} disabled={cancelling}>
            Voltar
          </Button>
          <Button
            onClick={handleCancelShipment}
            color='secondary'
            variant='contained'
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={20} /> : <CancelIcon />}
          >
            {cancelling ? 'Cancelando...' : 'Confirmar Cancelamento'}
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={creating} style={{ zIndex: 9999 }}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </>
  );
}
